import React from "react";

type FileLike =
    | string
    | {
    url?: string;
    link?: string;
    href?: string;
    name?: string;
    filename?: string;
    originalName?: string;
};

type NormalizedFile = {
    url: string;
    name: string;
};

function normalizeAttachmentUrl(rawUrl: string): string | null {
    const value = rawUrl.trim();
    if (!value) return null;

    try {
        const baseOrigin = typeof window !== "undefined"
            ? window.location.origin
            : "https://lkfr.chio-chio.ru";
        const resolved = new URL(value, baseOrigin);
        if (resolved.protocol !== "http:" && resolved.protocol !== "https:") {
            return null;
        }

        return resolved.toString();
    } catch {
        return null;
    }
}

function filenameFromUrl(url: string): string {
    try {
        const clean = decodeURIComponent(url.split("?")[0].split("#")[0]);
        const parts = clean.split("/");
        return parts[parts.length - 1] || "file";
    } catch {
        return "file";
    }
}

function getExtFromUrl(url: string): string {
    try {
        const clean = url.split("?")[0].split("#")[0];
        const parts = clean.split("/");
        const last = parts[parts.length - 1] || "";
        const dot = last.lastIndexOf(".");
        return dot >= 0 ? last.slice(dot + 1).toLowerCase() : "";
    } catch {
        return "";
    }
}

function normalizeFile(file: FileLike): NormalizedFile | null {
    if (!file) return null;
    if (typeof file === "string") {
        const url = normalizeAttachmentUrl(file);
        return url ? { url, name: filenameFromUrl(url) } : null;
    }

    const url = normalizeAttachmentUrl(file.url || file.link || file.href || "");
    if (!url) return null;

    return {
        url,
        name: file.name || file.filename || file.originalName || filenameFromUrl(url),
    };
}

function isImageUrl(url: string): boolean {
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(getExtFromUrl(url));
}

export const MessageAttachments: React.FC<{ files: any[] | null | undefined }> = ({ files }) => {
    const normalized = (Array.isArray(files) ? files : [])
        .map<NormalizedFile | null>((file) => normalizeFile(file as FileLike))
        .filter((file): file is NormalizedFile => Boolean(file));

    if (!normalized.length) return null;

    return (
        <div className="message-attachments">
            {normalized.map((file, index) => {
                const image = isImageUrl(file.url);

                return (
                    <div key={`${file.url}-${index}`} className="message-attach">
                        {image ? (
                            <>
                                <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="message-attach-thumb-wrap"
                                    title={file.name}
                                >
                                    <img className="message-attach-thumb" src={file.url} alt={file.name} loading="lazy" decoding="async" width={320} height={190} />
                                </a>
                                <div className="message-attach-link-only">
                                    <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                                        {file.name}
                                    </a>
                                </div>
                            </>
                        ) : (
                            <div className="message-attach-link-only message-attach-link-only--file">
                                <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                                    {file.name}
                                </a>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
